/** @jsx jsx */
import { CardHorizontalDivider, Flex, Grid, Paragraph, PopoverDisclosure, usePopover } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { PreviewFileEntry } from "@/components/Files/preview/PreviewFileEntry";
import { UserPopover } from "@/components/popovers/UserPopover";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { LifecycleAction } from "@/data/lifecycle";
import { formatFullName } from "@/data/persons";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";

export interface LifecycleCardProps {
  action: LifecycleAction;
}

export const LifecycleCard: FC<LifecycleCardProps> = ({ action }) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();
  const { formatDateTime } = useFormats();
  const userPopoverState = usePopover();

  const showDetails = [action.comment, action.externalComment, action.attachments?.length].some(e => !!e);

  return (
    <ResponsiveCard>
      <Grid columns={[null, null, null, "1fr 1fr 1fr"]} gap={["xs", null, null, "sm"]}>
        <Flex alignItems="center">
          <Paragraph size="p-md" bold>
            {tCodelist("lifecycle-action-performed", action.actionPerformed.code)}
          </Paragraph>
        </Flex>

        <div>
          {action.completedBy && (
            <React.Fragment>
              <PopoverDisclosure as={Paragraph} bold {...userPopoverState}>
                <Flex gap="xs" alignItems="center">
                  <LazyUserAvatar user={action.completedBy} size="xs" />
                  <Paragraph ellipsis>{formatFullName(action.completedBy)}</Paragraph>
                </Flex>
              </PopoverDisclosure>

              <UserPopover
                user={action.completedBy}
                state={userPopoverState}
                position={action.completedBy?.position?.name}
              />
            </React.Fragment>
          )}
        </div>

        <Flex justifyContent={["flex-start", null, null, "flex-end"]} alignItems="center">
          <Paragraph>{formatDateTime(action.completionDate)}</Paragraph>
        </Flex>
      </Grid>

      {showDetails && (
        <React.Fragment>
          <CardHorizontalDivider />

          <Grid columns={[null, null, null, "1fr 1fr 1fr"]}>
            <Flex flexDirection="column" gap="md" gridColumnStart={1} gridColumnEnd={3}>
              {(action.comment || action.externalComment) && (
                <Flex flexDirection="column" gap="xs">
                  <Paragraph size="p-md" color="n700">
                    {t("LIFECYCLE.COMMENT")}
                  </Paragraph>
                  <Paragraph>{action.comment ?? action.externalComment}</Paragraph>
                </Flex>
              )}

              {Boolean(action.attachments?.length) && (
                <Flex flexDirection="column" gap="xs">
                  <Paragraph size="p-md" color="n700">
                    {t("LIFECYCLE.ATTACHMENTS")}
                  </Paragraph>
                  <Grid columns={["1fr", null, null, "1fr 1fr"]}>
                    {action.attachments?.map(attachment => (
                      <PreviewFileEntry key={attachment.systemId} attachment={attachment}>
                        {attachment.name}
                      </PreviewFileEntry>
                    ))}
                  </Grid>
                </Flex>
              )}
            </Flex>
          </Grid>
        </React.Fragment>
      )}
    </ResponsiveCard>
  );
};
